import { Injectable } from '@angular/core';
import { PromptService } from '../promtp-service/prompt.service';
import { firstValueFrom, timeout } from 'rxjs';
import { PointsConsumerService } from 'src/app/api/proxy/points/points-services';
import { CurrentBalanceModel, PointsHistoryRequest, PointsHistoryResponse } from 'src/app/api/proxy/points/points-models';
import { SecureStorageService } from '../storage/secure-storage.service';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';

@Injectable({
  providedIn: 'root',
})
export class PointsService {
  private NETWORK_TIMEOUT = 25000;
  private cache: Map<string, PointsHistoryResponse> = new Map(); // Cache map to store responses
  public static MAX_CACHE_MONTHS = 12;

  constructor(
    protected _PromptService: PromptService,
    private _PoinsConsumer: PointsConsumerService
  ) {
    setTimeout(async () => { await this.loadCacheFromStorage() }, 0);
  }

  private async loadCacheFromStorage(): Promise<void> {
    const storedMap = await SecureStorageService.get(SecureStorageKey.PointsHistory);
    if (storedMap) {
      const parsedMap = JSON.parse(storedMap) as Record<string, PointsHistoryResponse>;
      this.cache = new Map(Object.entries(parsedMap));
    }
  }

  private async saveCacheToStorage(): Promise<void> {
    const cacheObject = Object.fromEntries(this.cache);
    await SecureStorageService.set(SecureStorageKey.PointsHistory, JSON.stringify(cacheObject));
  }

  public async loadMonth(yearNumber: number, month: number): Promise<PointsHistoryResponse | null> {
    await this.ensureCacheLoaded();
    const cacheKey = `${yearNumber}-${month}`;
    const currentMonthKey = this.getCurrentMonthKey();

    // Check if data is cached and return it
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) || null;
    }

    try {
      // Create request for the specified month
      const fromDate = new Date(yearNumber, month - 1, 1, 0, 0, 0);
      const endDate = new Date(yearNumber, month, 0, 23, 59, 59);
      const request: PointsHistoryRequest = {
        startDate: fromDate.toISOString(),
        endDate: endDate.toISOString(),
      };

      const result = await firstValueFrom(
        this._PoinsConsumer.consumerHistoryPost({ body: request })
          .pipe(timeout(this.NETWORK_TIMEOUT))
      );

      // Store result in cache if not the current month
      if (result && cacheKey !== currentMonthKey) {
        await this.addToCache(cacheKey, result);
      }

      return result;
    } catch (err) {
      await this._PromptService.showNetworkConnectionError(err, 'loadHistory');
      return null;
    }
  }

  public async currentBalance(forced:boolean): Promise<CurrentBalanceModel | null> {
    try {
      // Create request for the specified month
      const result = await firstValueFrom(
        this._PoinsConsumer.consumerCurrentBalanceGet()
          .pipe(timeout(this.NETWORK_TIMEOUT))
      );
      return result;
    } catch (err) {
      await this._PromptService.showNetworkConnectionError(err, 'loadHistory');
      return null;
    }
  }

  private async addToCache(cacheKey: string, data: PointsHistoryResponse): Promise<void> {
    if (this.cache.size >= PointsService.MAX_CACHE_MONTHS) {
      const oldestKey = Array.from(this.cache.keys())[0];
      this.cache.delete(oldestKey);
    }
    this.cache.set(cacheKey, data);
    await this.saveCacheToStorage();
  }

  private getCurrentMonthKey(): string {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}`;
  }

  private async ensureCacheLoaded(): Promise<void> {
    if (this.cache.size === 0) {
      await this.loadCacheFromStorage();
    }
  }
}
